import React, { useState, useEffect, useRef } from 'react';
import { HiSearch, HiSortAscending, HiSortDescending, HiOutlinePencil, HiOutlineTrash } from 'react-icons/hi';
import { PieChart, Pie, Cell, Tooltip } from 'recharts';
import Swal from 'sweetalert2'; // Importar SweetAlert2
import styles from '../../css/ControlPage.css';
import { db } from '../../firebaseconfig';
import { collection, getDocs, query } from 'firebase/firestore';
import ExpandableText from './ExpandableText';

const PostList = ({ posts, onSort, searchTerm, onEdit, onDelete, formatCurrency }) => {
  const [users, setUsers] = useState({});
  const [isScrollTopVisible, setIsScrollTopVisible] = useState(false);
  const tableContainerRef = useRef(null);
  const [sortBy, setSortBy] = useState('fecha');
  const [sortOrder, setSortOrder] = useState('desc');
  const [localSearchTerm, setLocalSearchTerm] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  const getUserDisplayName = (email) => {
    return users[email] || email;
  };

  const filteredPosts = posts.filter((post) => {
    const searchTermLower = localSearchTerm.toLowerCase();
    return (
      getUserDisplayName(post.usuario).toLowerCase().includes(searchTermLower) ||
      post.area.toLowerCase().includes(searchTermLower) ||
      post.obra.toLowerCase().includes(searchTermLower) ||
      post.costo.toString().includes(searchTermLower) ||
      post.state.toLowerCase().includes(searchTermLower) ||
      post.fecha.toLowerCase().includes(searchTermLower)
    );
  });

  const sortedPosts = [...filteredPosts].sort((a, b) => {
    if (sortBy === 'fecha') {
      return sortOrder === 'asc' ? new Date(a.fecha) - new Date(b.fecha) : new Date(b.fecha) - new Date(a.fecha);
    } else if (sortBy === 'obra') {
      return sortOrder === 'asc' 
        ? a.obra.localeCompare(b.obra, undefined, { sensitivity: 'base' })
        : b.obra.localeCompare(a.obra, undefined, { sensitivity: 'base' });
    } else if (sortBy === 'costo') {
      return sortOrder === 'asc' ? a.costo - b.costo : b.costo - a.costo;
    }
    return 0;
  });

  const fetchUsers = async () => {
    try {
      const usersQuery = query(collection(db, 'users'));
      const querySnapshot = await getDocs(usersQuery);
      const usersData = {};
      querySnapshot.forEach((doc) => {
        const userData = doc.data();
        usersData[userData.email] = userData.name;
      });
      setUsers(usersData);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleScroll = () => {
    if (tableContainerRef.current.scrollTop > 300) {
      setIsScrollTopVisible(true);
    } else {
      setIsScrollTopVisible(false);
    }
  };

  const handleSort = (e) => {
    const [newSortBy, newSortOrder] = e.target.value.split('-');
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    if (onSort) {
      onSort(newSortBy, newSortOrder);
    }
  };

  const handleLocalSearch = (e) => {
    setLocalSearchTerm(e.target.value);
    setHasSearched(e.target.value !== ''); // Cambia el estado basado en si hay búsqueda o no
  };

  const scrollToTop = () => {
    tableContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    const tableContainer = tableContainerRef.current;
    if (tableContainer) {
      tableContainer.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (tableContainer) {
        tableContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  const confirmDelete = (post) => {
    Swal.fire({
      title: '¿Estás seguro?',
      text: '¡No podrás deshacer esta acción!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, borrar',
      cancelButtonText: 'Cancelar',
    }).then((result) => {
      if (result.isConfirmed) {
        onDelete(post); // Llamar a la función de borrado sólo si se confirma
        Swal.fire('¡Borrado!', 'El registro ha sido eliminado.', 'success');
      }
    });
  };

  return (
    <div>
      <div className="controls">
        <div className="search-and-sort-container">
          <div className="search-box">
            <button className="btn-search"><HiSearch /></button>
            <input
              type="text"
              className="input-search"
              placeholder="Buscar por usuario, área, proyecto, inversión, estado o fecha..."
              value={localSearchTerm}
              onChange={handleLocalSearch}
            />
          </div>
          <div className="search-container">
            <select onChange={handleSort} value={`${sortBy}-${sortOrder}`} className="sort-select">
              <option value="fecha-desc">Fecha (Más reciente)</option>
              <option value="fecha-asc">Fecha (Más antiguo)</option>
              <option value="obra-asc">Proyecto (A-Z)</option>
              <option value="obra-desc">Proyecto (Z-A)</option>
              <option value="costo-asc">Inversión (Menor a Mayor)</option>
              <option value="costo-desc">Inversión (Mayor a Menor)</option>
            </select>
          </div>
        </div>
      </div>

      <div 
        className="table-container" 
        ref={tableContainerRef} 
        style={{ maxHeight: '400px', overflowY: 'auto' }}
      >
        <table>
          <thead>
            <tr className="header-row">
              <th>Fecha</th>
              <th>Usuario</th>
              <th>Área</th>
              <th>Proyecto</th>
              <th>Inversión total</th>
              <th>Descripción</th>
              <th>Estado</th>
              <th>Avance</th>
              <th>Gráfico</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {posts.length === 0 ? (
              <tr>
                <td colSpan="10" style={{ textAlign: 'center', padding: '20px' }}>
                  No hay publicaciones disponibles.
                </td>
              </tr>
            ) : sortedPosts.length === 0 && localSearchTerm ? (
              <tr>
                <td colSpan="10" style={{ textAlign: 'center', padding: '20px' }}>
                  No se encontraron resultados para "{localSearchTerm}"
                </td>
              </tr>
            ) : (
              sortedPosts.map((post) => (
                <tr className="table-row" key={post.id}>
                  <td>{post.fecha}</td>
                  <td>{getUserDisplayName(post.usuario)}</td>
                  <td>{post.area}</td>
                  <td>{post.obra}</td>
                  <td>{formatCurrency(post.costo)}</td>
                  <td>
                    <ExpandableText text={post.descripcion} />
                  </td>
                  <td>{post.state}</td>
                  <td>{post.percentage}%</td>
                  <td>
                    <PieChart width={80} height={80}>
                      <Pie
                        data={[
                          { name: 'Porcentaje', value: post.percentage || 0 },
                          { name: 'Resto', value: 100 - (post.percentage || 0) },
                        ]}
                        cx="50%"
                        cy="50%"
                        outerRadius={30}
                        dataKey="value"
                      >
                        <Cell key="Porcentaje" fill="#82ca9d" />
                        <Cell key="Resto" fill="#ccc" />
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </td>
                  <td>
                    <button className="edit-button" onClick={() => onEdit(post)}>
                      <HiOutlinePencil />
                    </button>
                    <button className="delete-button" onClick={() => confirmDelete(post)}>
                      <HiOutlineTrash />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>

        </table>
      </div>

      {isScrollTopVisible && (
        <button 
          className="scroll-to-top-button" 
          onClick={scrollToTop} 
        >
          ↑
        </button>
      )}
    </div>
  );
};

export default PostList;
